/**
 * Pure helpers for the private sale (VIP) campaign eligibility (EIFE-3517 / EIFE-3659).
 * The published `eligible` Amplience content holds the campaign CL tags. The email
 * allow-lists live in `eligible-allowlist` contents that are never published: they
 * are read at build time through the Management API, so they never reach the CDN.
 */

export const ELIGIBLE_SCHEMA_ID = 'https://armani.com/eligible'

export const ELIGIBLE_ALLOWLIST_SCHEMA_ID =
  'https://armani.com/eligible-allowlist'

export const normalizeEmail = (email: string) => email.trim().toLowerCase()

/**
 * Normalized email allow-list. Each entry may also contain several emails
 * separated by commas, semicolons or whitespace (editors pasting a list).
 */
export const parseAllowedEmails = (value: unknown): string[] => {
  const entries = Array.isArray(value) ? value : [value]
  return entries
    .filter((entry): entry is string => typeof entry === 'string')
    .flatMap(entry => entry.split(/[\s,;]+/))
    .map(normalizeEmail)
    .filter(Boolean)
}

/** Case-insensitive and trimmed match of an email against the allow-list. */
export const isEmailAllowed = (email: unknown, allowList: unknown) => {
  if (typeof email !== 'string') return false
  const normalized = normalizeEmail(email)
  if (!normalized) return false
  return parseAllowedEmails(allowList).includes(normalized)
}

/** Pipe-separated eligible tags, as configured on the `eligible` content. */
export const parseEligibleTags = (tags: unknown): string[] =>
  typeof tags === 'string'
    ? tags
        .split('|')
        .map(tag => tag.trim())
        .filter(Boolean)
    : []

/**
 * Virtual tag: satisfied by any logged-in customer, without waiting for the
 * Commerce Layer webhook that assigns it some seconds after registration.
 */
export const REGISTERED_TAG = 'registered'

/** True when the eligible tags can be satisfied by the session alone. */
export const isOpenToRegisteredCustomers = (eligibleTags: string[]) =>
  eligibleTags.includes(REGISTERED_TAG)

/**
 * Access rule for tag-gated content: no tags means no restriction,
 * `registered` only requires a logged-in customer, any other tag must be
 * assigned to the customer.
 */
export const hasEligibleAccess = ({
  eligibleTags,
  customerTags,
  isLogged,
}: {
  eligibleTags: string[]
  customerTags: string[] | null | undefined
  isLogged: boolean
}) => {
  if (!eligibleTags.length) return true
  if (!isLogged) return false
  if (isOpenToRegisteredCustomers(eligibleTags)) return true
  return eligibleTags.some(tag => customerTags?.includes(tag) ?? false)
}

/**
 * Tag relationship ids for a customer update: keeps every existing tag and
 * adds the new one only once (idempotent).
 */
export const mergeTagIds = (currentIds: string[], tagId: string) =>
  Array.from(new Set([...currentIds, tagId]))

/**
 * Normalized allow-lists keyed by the delivery id of the `eligible` content they
 * refer to, from `eligible-allowlist` bodies. Lists of the same campaign are merged.
 */
export const groupAllowListsByEligible = (
  bodies: unknown[]
): Record<string, string[]> => {
  const allowLists = new Map<string, Set<string>>()
  for (const body of bodies) {
    const { eligible, allowedEmails } = (body ?? {}) as {
      eligible?: { id?: unknown }
      allowedEmails?: unknown
    }
    if (typeof eligible?.id !== 'string' || !eligible.id) continue

    const emails = allowLists.get(eligible.id) ?? new Set<string>()
    parseAllowedEmails(allowedEmails).forEach(email => emails.add(email))
    if (emails.size) allowLists.set(eligible.id, emails)
  }
  return Object.fromEntries(
    Array.from(allowLists, ([eligibleId, emails]) => [eligibleId, [...emails]])
  )
}

// ASCII control chars are dropped by browsers when resolving a Location
// ("/\t/evil.com" -> "//evil.com") or make setHeader throw; backslashes are
// read as slashes. Their percent-encoded forms are rejected too.
const hasControlChar = (value: string) =>
  Array.from(value).some(char => {
    const code = char.charCodeAt(0)
    return code <= 0x1f || code === 0x7f
  })
const UNSAFE_ENCODED_CHARS = /%(?:[01][0-9a-f]|7f|5c)/i
const REDIRECT_CHECK_ORIGIN = 'https://redirect-check.invalid'

/** Only same-origin absolute paths are accepted as post-login redirects. */
export const isSafeRedirectPath = (path: unknown): path is string => {
  if (
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.includes('\\') ||
    hasControlChar(path) ||
    UNSAFE_ENCODED_CHARS.test(path)
  ) {
    return false
  }
  try {
    return new URL(path, REDIRECT_CHECK_ORIGIN).origin === REDIRECT_CHECK_ORIGIN
  } catch {
    return false
  }
}

/** Amplience delivery key, e.g. `eligible/ax-exclusive`. */
export const isValidDeliveryKey = (key: unknown): key is string =>
  typeof key === 'string' &&
  /^[\w.-]+(\/[\w.-]+)*$/.test(key) &&
  !key.includes('..') &&
  key.length <= 150
